import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@baseline/web/supabase";

export interface ProjectDocument {
	id: string;
	title: string | null;
	doc_type: string | null;
	summary: string | null;
	content: string | null;
	added_at: string;
	updated_at: string | null;
}

export interface ProjectSource {
	id: string;
	title: string | null;
	item_type: string;
	content_id: string | null;
	added_at: string;
	contents: {
		id: string;
		title: string | null;
		canonical_url: string;
		og_image_url: string | null;
		domain: string | null;
		channel_name: string | null;
	} | null;
}

export interface ProjectDetail {
	id: string;
	name: string;
	description: string | null;
	last_synthesized_at: string | null;
	synthesis_due_at: string | null;
	synthesisDoc: ProjectDocument | null;
	documents: ProjectDocument[];
	sources: ProjectSource[];
}

// CONTEXT: Generated Supabase types are stale — project_id on library_items and
// synthesis columns on projects were added in recent migrations not yet reflected
// in the generated types. We cast query results until types are regenerated.

interface ProjectRow {
	id: string;
	name: string;
	description: string | null;
	last_synthesized_at: string | null;
	synthesis_due_at: string | null;
}

interface ItemRow {
	id: string;
	title: string | null;
	doc_type: string | null;
	summary: string | null;
	content: string | null;
	source_type: string;
	item_type: string;
	content_id: string | null;
	added_at: string;
	updated_at: string | null;
	contents: ProjectSource["contents"];
}

export function useProject(projectId: string, userId: string | undefined) {
	const queryClient = useQueryClient();

	const query = useQuery({
		queryKey: ["project", projectId],
		queryFn: async (): Promise<ProjectDetail | null> => {
			const [projectRes, itemsRes] = await Promise.all([
				supabase
					.from("projects")
					.select(
						"id, name, description, last_synthesized_at, synthesis_due_at",
					)
					.eq("id", projectId)
					.single(),
				supabase
					.from("library_items")
					.select(
						`
						id, title, doc_type, summary, content, source_type,
						item_type, content_id, added_at, updated_at, is_archived,
						contents (
							id, title, canonical_url, og_image_url, domain, channel_name
						)
					`,
					)
					.eq("project_id", projectId)
					.eq("is_archived", false)
					.order("added_at", { ascending: false }),
			]);

			if (projectRes.error) {
				if (projectRes.error.code === "PGRST116") return null;
				throw projectRes.error;
			}
			if (itemsRes.error) throw itemsRes.error;

			const project = projectRes.data as unknown as ProjectRow;
			const items = (itemsRes.data ?? []) as unknown as ItemRow[];

			// Split items into documents and sources
			let synthesisDoc: ProjectDocument | null = null;
			const documents: ProjectDocument[] = [];
			const sources: ProjectSource[] = [];

			for (const item of items) {
				if (item.source_type === "document") {
					const doc: ProjectDocument = {
						id: item.id,
						title: item.title,
						doc_type: item.doc_type,
						summary: item.summary,
						content: item.content,
						added_at: item.added_at,
						updated_at: item.updated_at,
					};
					if (item.doc_type === "synthesis") {
						synthesisDoc = doc;
					} else {
						documents.push(doc);
					}
				} else {
					sources.push({
						id: item.id,
						title: item.title,
						item_type: item.item_type,
						content_id: item.content_id,
						added_at: item.added_at,
						contents: item.contents,
					});
				}
			}

			return {
				id: project.id,
				name: project.name,
				description: project.description,
				last_synthesized_at: project.last_synthesized_at,
				synthesis_due_at: project.synthesis_due_at,
				synthesisDoc,
				documents,
				sources,
			};
		},
		enabled: !!userId,
	});

	useEffect(() => {
		if (!userId) return;

		const channel = supabase
			.channel(`project-detail:${projectId}`)
			.on(
				"postgres_changes",
				{
					event: "*",
					schema: "public",
					table: "library_items",
					filter: `project_id=eq.${projectId}`,
				},
				() => {
					queryClient.invalidateQueries({
						queryKey: ["project", projectId],
					});
				},
			)
			.on(
				"postgres_changes",
				{
					event: "UPDATE",
					schema: "public",
					table: "projects",
					filter: `id=eq.${projectId}`,
				},
				() => {
					queryClient.invalidateQueries({
						queryKey: ["project", projectId],
					});
				},
			)
			.subscribe();

		return () => {
			supabase.removeChannel(channel);
		};
	}, [projectId, userId, queryClient]);

	return query;
}
